/**
 * Página de listagem de produtos (visualização)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Layout, Loading, ErrorMessage, SearchBar, EmptyState, PageHeader } from '@components';
import { ProdutoCard } from '@components/produto/ProdutoCard';
import { useFetch, useFilter } from '@hooks';
import { produtoController, classificacaoController } from '@controllers';
import { Inventory } from '@mui/icons-material';
import { Produto } from '@types';
import { CacheUtil } from '@utils';
import theme from '@theme';

type OrderBy = 'nome-asc' | 'nome-desc' | 'preco-asc' | 'preco-desc';

export const ListaProdutoPage: React.FC = () => {
  const { idClassificacao } = useParams<{ idClassificacao: string }>();
  const navigate = useNavigate();
  const [produtos, setProdutos] = useState<Produto[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');

  // Carrega classificação
  const { data: classificacao } = useFetch(
    () =>
      idClassificacao
        ? classificacaoController.obterPorCodigo(idClassificacao)
        : Promise.resolve(null),
    { key: idClassificacao }
  );

  // Carrega produtos com cache (usa tipo 'visualizacao' como identificador)
  useEffect(() => {
    const loadProdutos = async () => {
      if (!idClassificacao) {
        setProdutos([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Tenta carregar do cache primeiro
        const cached = CacheUtil.loadProdutosCache('previsao', `view-${idClassificacao}`);
        if (cached) {
          console.log('Produtos (visualização) carregados do cache');
          setProdutos(cached);
          setIsLoading(false);
          return;
        }

        // Busca da API (sem parâmetro de busca - carrega todos)
        console.log('Carregando produtos (visualização) da API...');
        const result = await produtoController.obterProdutos(idClassificacao);
        setProdutos(result);

        // Salva no cache
        if (result.length > 0) {
          CacheUtil.saveProdutosCache('previsao', `view-${idClassificacao}`, result);
          console.log('Produtos (visualização) salvos no cache');
        }
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProdutos();
  }, [idClassificacao]);

  const refetch = useCallback(() => {
    // Invalida cache e recarrega
    if (idClassificacao) {
      CacheUtil.invalidateProdutosCache('previsao', `view-${idClassificacao}`);
      window.location.reload();
    }
  }, [idClassificacao]);

  // Filtra produtos localmente
  const { filteredData, searchTerm, setSearchTerm } = useFilter(produtos || [], ['nome', 'codigo']);

  // Ordena produtos
  const sortedData = React.useMemo(() => {
    if (!filteredData) return [];

    const [field, direction] = orderBy.split('-') as ['nome' | 'preco', 'asc' | 'desc'];

    if (field === 'nome') {
      return produtoController.ordenarPorNome(filteredData, direction);
    } else {
      return produtoController.ordenarPorPreco(filteredData, direction);
    }
  }, [filteredData, orderBy]);

  const handleAddToCart = (produtoId: string) => {
    // Por enquanto apenas mostra o ID
    console.log('Adicionar produto ao carrinho:', produtoId);
    // Futura integração com carrinho
  };

  if (isLoading) {
    return (
      <Layout title="Produtos">
        <Loading message="Carregando produtos..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Produtos">
        <ErrorMessage
          title="Erro ao carregar produtos"
          message={error.message}
          onRetry={refetch}
        />
      </Layout>
    );
  }

  return (
    <Layout title={classificacao?.nome || 'Produtos'} showHeader showFooter footerType="default">
      <PageHeader
        title={classificacao?.nome || 'Produtos'}
        subtitle={`${sortedData.length} produtos encontrados`}
        showBackButton
        onBack={() => navigate('/classificacoes')}
      />

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar produto..."
          />
        </Box>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="preco-asc">Menor preço</MenuItem>
            <MenuItem value="preco-desc">Maior preço</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {sortedData.length === 0 ? (
        <EmptyState
          title="Nenhum produto encontrado"
          message={
            searchTerm
              ? 'Tente ajustar sua busca'
              : 'Não há produtos nesta categoria'
          }
          icon={<Inventory />}
        />
      ) : (
        <Grid container spacing={2}>
          {sortedData.map((produto) => {
            const disponivel = produtoController.calcularDisponibilidade(produto);
            return (
              <Grid item xs={6} sm={6} md={3} key={produto.codigo}>
                <ProdutoCard
                  produto={produto}
                  tipo="visualizacao"
                  disponivel={disponivel}
                  onAddToCart={() => handleAddToCart(produto.codigo)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Layout>
  );
};

export default ListaProdutoPage;